/**
 * Utilidad para procesar y extraer información de logs de órdenes de producción
 */
class LogsProcessor {
  /**
   * Extrae información de procesos desde logs de plan de centro de costo
   * @param {Array} logs - Array de logs de plan de centro de costo
   * @returns {Array} Array de procesos con información extraída
   */
  static extraerInformacionProcesos(logs) {
    if (!Array.isArray(logs)) return [];

    return logs.map(log => {
      // Los datos del centro_costo ya están expandidos directamente en el log
      const centroCosto = log?.expand?.centro_costo;
      const responsable = this.extraerResponsable(centroCosto);

      // Calcular tiempo del proceso en horas
      const tiempoProceso = this.calcularTiempoProceso(log?.inicio, log?.fin);

      return {
        id: log?.id || 'N/A',
        nombre: centroCosto?.nombre || 'N/A',
        centro_costo: centroCosto?.nombre || 'N/A',
        operario: responsable?.includes('Proveedor') ? null : responsable,
        proveedor: responsable?.includes('Proveedor') ? responsable : null,
        fecha_inicio: log?.inicio || null,
        fecha_fin: log?.fin || null,
        orden_plan: log?.orden_plan || 0,
        porcentaje: log?.porcentaje || 0,
        porcentaje_tiempo: log?.porcentaje || 0, // Agregar para compatibilidad con el componente
        tiempo_proceso: tiempoProceso,
        estado: this.determinarEstadoProceso(log?.inicio, log?.fin)
      };
    });
  }

  /**
   * Calcula el tiempo de un proceso en horas
   * @param {string} fechaInicio - Fecha de inicio
   * @param {string} fechaFin - Fecha de fin
   * @returns {number} Tiempo en horas
   */
  static calcularTiempoProceso(fechaInicio, fechaFin) {
    if (!fechaInicio || !fechaFin) return 0;
    
    try {
      const inicio = new Date(fechaInicio);
      const fin = new Date(fechaFin);
      
      if (isNaN(inicio.getTime()) || isNaN(fin.getTime())) return 0;
      
      const diffMs = fin.getTime() - inicio.getTime();
      if (diffMs < 0) return 0;
      
      return diffMs / (1000 * 60 * 60); // Convertir a horas
    } catch (error) {
      return 0;
    }
  }

  /**
   * Determina el estado de un proceso basado en las fechas
   * @param {string} fechaInicio - Fecha de inicio
   * @param {string} fechaFin - Fecha de fin
   * @returns {string} Estado del proceso
   */
  static determinarEstadoProceso(fechaInicio, fechaFin) {
    if (!fechaInicio) return 'Pendiente';
    if (!fechaFin) return 'En Proceso';
    return 'Completado';
  }

  /**
   * Extrae información de logs de estado
   * @param {Array} logsEstado - Array de logs de estado
   * @returns {Array} Array de logs de estado con información extraída
   */
  static extraerInformacionLogsEstado(logsEstado) {
    if (!Array.isArray(logsEstado)) return [];

    return logsEstado.map(log => {
      const usuario = log?.expand?.usuario?.name || null;
      const proveedor = log?.expand?.proveedor?.nombre || null;

      return {
        id: log?.id || 'N/A',
        accion: log?.accion || 'N/A',
        fecha_estado: log?.fecha_estado || null,
        usuario,
        proveedor
      };
    });
  }

  /**
   * Extrae el responsable (operario o proveedor) de un centro de costo
   * @param {Object} centroCosto - Datos del centro de costo
   * @returns {string|null} Nombre del responsable o null
   */
  static extraerResponsable(centroCosto) {
    if (!centroCosto || !centroCosto.expand) return null;

    // Priorizar operario sobre proveedor
    if (centroCosto.operario && centroCosto.expand.operario) {
      return centroCosto.expand.operario.name || centroCosto.expand.operario.nombre;
    }

    if (centroCosto.proceso_proveedor && centroCosto.expand.proceso_proveedor) {
      return centroCosto.expand.proceso_proveedor.nombre || centroCosto.expand.proceso_proveedor.name;
    }

    return null;
  }

  /**
   * Procesa logs completos (procesos y estado) y extrae información
   * @param {Array} logsProcesos - Array de logs de procesos
   * @param {Array} logsEstado - Array de logs de estado
   * @returns {Object} Objeto con información procesada
   */
  static procesarLogsCompletos(logsProcesos, logsEstado) {
    const procesos = this.extraerInformacionProcesos(logsProcesos || []);
    const logsEstadoProcesados = this.extraerInformacionLogsEstado(logsEstado || []);

    return {
      procesos,
      logsEstado: logsEstadoProcesados,
      metadata: {
        procesadosEn: new Date().toISOString(),
        totalProcesos: procesos.length,
        totalLogsEstado: logsEstadoProcesados.length
      }
    };
  }

  /**
   * Valida la estructura de los logs
   * @param {Array} logs - Array de logs a validar
   * @returns {Object} Resultado de la validación
   */
  static validarLogs(logs) {
    const errores = [];

    if (!Array.isArray(logs)) {
      errores.push('Los logs deben ser un array');
      return { esValido: false, errores };
    }

    logs.forEach((log, index) => {
      if (!log || typeof log !== 'object') {
        errores.push(`Log en índice ${index} no es un objeto válido`);
        return;
      }

      if (!log.id) {
        errores.push(`Log en índice ${index} no tiene ID`);
      }

      if (!log.orden && !log.orden_produccion) {
        errores.push(`Log en índice ${index} no tiene referencia a orden`);
      }
    });

    return {
      esValido: errores.length === 0,
      errores
    };
  }

  /**
   * Filtra logs por rango de fechas
   * @param {Array} logs - Array de logs a filtrar
   * @param {string} fechaInicio - Fecha de inicio (ISO string)
   * @param {string} fechaFin - Fecha de fin (ISO string)
   * @returns {Array} Logs filtrados por fecha
   */
  static filtrarLogsPorFecha(logs, fechaInicio, fechaFin) {
    if (!Array.isArray(logs) || !fechaInicio || !fechaFin) return [];

    try {
      const inicio = new Date(fechaInicio);
      const fin = new Date(fechaFin);

      if (isNaN(inicio.getTime()) || isNaN(fin.getTime())) return [];

      return logs.filter(log => {
        const fechaLog = new Date(log.fecha_estado || log.inicio || log.fin);
        return fechaLog >= inicio && fechaLog <= fin;
      });
    } catch (error) {
      return [];
    }
  }

  /**
   * Agrupa logs por tipo de acción
   * @param {Array} logsEstado - Array de logs de estado
   * @returns {Object} Logs agrupados por acción
   */
  static agruparLogsPorAccion(logsEstado) {
    if (!Array.isArray(logsEstado)) return {};

    return logsEstado.reduce((grupos, log) => {
      const accion = log.accion || 'sin_accion';
      if (!grupos[accion]) {
        grupos[accion] = [];
      }
      grupos[accion].push(log);
      return grupos;
    }, {});
  }

  /**
   * Obtiene el primer log de una acción específica
   * @param {Array} logsEstado - Array de logs de estado
   * @param {string} accion - Acción a buscar
   * @returns {Object|null} Primer log de la acción o null
   */
  static obtenerPrimerLogAccion(logsEstado, accion) {
    if (!Array.isArray(logsEstado) || !accion) return null;

    return logsEstado.find(log => log.accion === accion) || null;
  }

  /**
   * Obtiene el último log de una acción específica
   * @param {Array} logsEstado - Array de logs de estado
   * @param {string} accion - Acción a buscar
   * @returns {Object|null} Último log de la acción o null
   */
  static obtenerUltimoLogAccion(logsEstado, accion) {
    if (!Array.isArray(logsEstado) || !accion) return null;

    const logsAccion = logsEstado.filter(log => log.accion === accion);
    return logsAccion.length > 0 ? logsAccion[logsAccion.length - 1] : null;
  }

  /**
   * Calcula estadísticas de logs
   * @param {Array} logs - Array de logs
   * @returns {Object} Estadísticas de los logs
   */
  static calcularEstadisticasLogs(logs) {
    if (!Array.isArray(logs)) {
      return {
        total: 0,
        porAccion: {},
        rangoFechas: { inicio: null, fin: null }
      };
    }

    const porAccion = this.agruparLogsPorAccion(logs);
    const fechas = logs
      .map(log => new Date(log.fecha_estado || log.inicio || log.fin))
      .filter(fecha => !isNaN(fecha.getTime()))
      .sort((a, b) => a - b);

    return {
      total: logs.length,
      porAccion: Object.keys(porAccion).reduce((acc, accion) => {
        acc[accion] = porAccion[accion].length;
        return acc;
      }, {}),
      rangoFechas: {
        inicio: fechas.length > 0 ? fechas[0].toISOString() : null,
        fin: fechas.length > 0 ? fechas[fechas.length - 1].toISOString() : null
      }
    };
  }

  /**
   * Ordena logs por fecha
   * @param {Array} logs - Array de logs a ordenar
   * @param {string} campoFecha - Campo de fecha a usar para ordenar
   * @param {boolean} ascendente - Si ordenar de forma ascendente (true) o descendente (false)
   * @returns {Array} Logs ordenados por fecha
   */
  static ordenarLogsPorFecha(logs, campoFecha = 'fecha_estado', ascendente = true) {
    if (!Array.isArray(logs)) return [];

    return [...logs].sort((a, b) => {
      const fechaA = new Date(a[campoFecha]);
      const fechaB = new Date(b[campoFecha]);

      if (isNaN(fechaA.getTime()) || isNaN(fechaB.getTime())) return 0;

      return ascendente ? fechaA - fechaB : fechaB - fechaA;
    });
  }

  /**
   * Busca logs por criterios específicos
   * @param {Array} logs - Array de logs a buscar
   * @param {Object} criterios - Criterios de búsqueda
   * @returns {Array} Logs que coinciden con los criterios
   */
  static buscarLogs(logs, criterios) {
    if (!Array.isArray(logs) || !criterios || typeof criterios !== 'object') {
      return [];
    }

    return logs.filter(log => {
      return Object.keys(criterios).every(key => {
        const valorCriterio = criterios[key];
        const valorLog = log[key];

        if (typeof valorCriterio === 'string' && typeof valorLog === 'string') {
          return valorLog.toLowerCase().includes(valorCriterio.toLowerCase());
        }

        return valorLog === valorCriterio;
      });
    });
  }
}

export default LogsProcessor;
